import { EventInput } from "@fullcalendar/core";
import { EventContentArg } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/react";
import { Box, Typography, List, ListItem, ListItemText, CardMedia, Tooltip } from "@mui/material";
import { motion } from "framer-motion";
import { Twitter, Facebook, Instagram } from "lucide-react";
import React, { useMemo } from "react";

import { PublicationDate, Metrics, Post } from "0.Mine/3.Interfaces/PostResponse";

// Icon & color mappings per platform - update keys to lowercase
const networkConfig: Record<string, { icon: React.ElementType; color: string }> = {
  twitter: { icon: Twitter, color: "#1DA1F2" },
  facebook: { icon: Facebook, color: "#1877F2" },
  instagram: { icon: Instagram, color: "#E1306C" },
};

// Narrowed PostEvent for FullCalendar
interface PostEvent extends EventInput {
  extendedProps: {
    text: string;
    metrics: Metrics;
    link?: string;
    picture?: string;
  };
}

type Props = {
  posts?: Post[];
};

const ContentCalendar: React.FC<Props> = ({ posts = [] }) => {
  const events = useMemo<PostEvent[]>(
    () =>
      posts.map((post) => ({
        id: post.id,
        title: post.network,
        start: post.publicationDate.dateTime,
        extendedProps: {
          text: post.text,
          metrics: post.metrics,
          link: post.link,
          picture: post.picture,
        },
        // Add order property for consistent stacking
        order:
          post.network.toLowerCase() === "facebook"
            ? 1
            : post.network.toLowerCase() === "instagram"
            ? 2
            : 3,
        display: "block", // Force block display
      })),
    [posts]
  );

  // Render event with updated styling and logo
  const renderEventContent = ({ event }: EventContentArg) => {
    const { extendedProps } = event as unknown as PostEvent;

    // Convert network name to lowercase for consistent lookup
    const networkName = event.title.toLowerCase();
    const config = networkConfig[networkName] || {
      icon: Twitter,
      color: "#666", // Default fallback
    };

    const Icon = config.icon;
    const bgColor = config.color || "#666";

    // Tooltip content with animated metrics
    const tooltipContent = (
      <Box sx={{ p: 1, maxWidth: 280 }}>
        {extendedProps.picture && (
          <CardMedia
            component="img"
            image={extendedProps.picture}
            alt="Post preview"
            sx={{
              height: "120px",
              width: "100%",
              objectFit: "contain",
              backgroundColor: "#f5f5f5",
              borderRadius: 1,
              mb: 1,
              maxHeight: 150,
              boxShadow: "0px 2px 4px rgba(0,0,0,0.1)",
            }}
          />
        )}
        <Typography variant="body2" gutterBottom noWrap>
          {extendedProps.text}
        </Typography>
        <List dense>
          {extendedProps.metrics.IMPRESSIONS != null && (
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <ListItem disableGutters>
                <ListItemText
                  primary={<Typography fontWeight="bold">Impressions</Typography>}
                  secondary={
                    <Typography variant="h6">{extendedProps.metrics.IMPRESSIONS}</Typography>
                  }
                />
              </ListItem>
            </motion.div>
          )}
          {extendedProps.metrics.INTERACTIONS != null && (
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.1, type: "spring", stiffness: 300 }}
            >
              <ListItem disableGutters>
                <ListItemText
                  primary={<Typography fontWeight="bold">Interactions</Typography>}
                  secondary={
                    <Typography variant="h6">{extendedProps.metrics.INTERACTIONS}</Typography>
                  }
                />
              </ListItem>
            </motion.div>
          )}
          {extendedProps.metrics.ENGAGEMENT != null && (
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 300 }}
            >
              <ListItem disableGutters>
                <ListItemText
                  primary={<Typography fontWeight="bold">Engagement</Typography>}
                  secondary={
                    <Typography variant="h6">{extendedProps.metrics.ENGAGEMENT}</Typography>
                  }
                />
              </ListItem>
            </motion.div>
          )}
        </List>
      </Box>
    );

    return (
      <Tooltip
        title={tooltipContent}
        disableInteractive={false}
        placement="bottom-start"
        arrow
        enterDelay={200}
        leaveDelay={100}
        sx={{
          "& .MuiTooltip-tooltip": {
            bgcolor: "background.paper",
            color: "text.primary",
            boxShadow: 3,
            borderRadius: 2,
            p: 1,
          },
        }}
      >
        <Box
          sx={{
            p: 1,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            backgroundColor: bgColor,
            borderRadius: 2,
            boxShadow: 1,
            "&:hover": {
              filter: "brightness(1.1)",
            },
            minWidth: "120px",
            justifyContent: "center",
          }}
        >
          {Icon && <Icon size={20} color="white" />}
          <Typography
            variant="body2"
            noWrap
            sx={{ color: "white", fontWeight: 600, fontSize: "0.75rem" }}
          >
            {event.title}
          </Typography>
        </Box>
      </Tooltip>
    );
  };

  return (
    <FullCalendar
      plugins={[dayGridPlugin, interactionPlugin]}
      initialView="dayGridMonth"
      events={events}
      eventContent={renderEventContent}
      height="auto"
      dayMaxEventRows={true}
      eventDisplay="block"
      locale="it"
      dayCellClassNames={() => "fc-day-cell"}
      eventClassNames={() => "fc-custom-event"}
      eventOrder="order"
      eventOverlap={false}
    />
  );
};

export default ContentCalendar;
